import React, { useState, useEffect, useCallback } from 'react'
import { salesChannelAssignmentsApi, CHANNEL_OPTIONS } from '../../services/salesChannelAssignments'
import type { UserWithChannels } from '../../services/salesChannelAssignments'
import { Toast } from '../shared/Toast'

export function SalesChannelSettings(): React.ReactElement {
  const [users, setUsers] = useState<UserWithChannels[]>([])
  const [channelMap, setChannelMap] = useState<Map<string, Set<string>>>(new Map())
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null)
  const [hasChanges, setHasChanges] = useState(false)

  const showToast = useCallback((message: string, type: 'success' | 'error') => {
    setToast({ message, type })
  }, [])

  const buildChannelMap = useCallback((userList: UserWithChannels[]): Map<string, Set<string>> => {
    const map = new Map<string, Set<string>>()
    userList.forEach((user) => {
      map.set(user.user_id, new Set(user.channels))
    })
    return map
  }, [])

  const loadSettings = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await salesChannelAssignmentsApi.index()
      setUsers(data.users)
      setChannelMap(buildChannelMap(data.users))
      setHasChanges(false)
    } catch {
      setError('Failed to load sales channel assignments. Please try again.')
    } finally {
      setLoading(false)
    }
  }, [buildChannelMap])

  useEffect(() => {
    loadSettings()
  }, [loadSettings])

  const handleToggle = (userId: string, channelName: string) => {
    setChannelMap((prev) => {
      const next = new Map(prev)
      const channels = new Set<string>(next.get(userId) || new Set())
      if (channels.has(channelName)) {
        channels.delete(channelName)
      } else {
        channels.add(channelName)
      }
      next.set(userId, channels)
      return next
    })
    setHasChanges(true)
  }

  const handleSave = async () => {
    try {
      setSaving(true)
      const assignments = Array.from(channelMap.entries()).map(([user_id, channels]) => ({
        user_id,
        channel_names: Array.from(channels),
      }))
      const data = await salesChannelAssignmentsApi.bulkUpdate(assignments)
      setUsers(data.users)
      setChannelMap(buildChannelMap(data.users))
      setHasChanges(false)
      showToast('Sales channel assignments saved', 'success')
    } catch {
      showToast('Failed to save sales channel assignments', 'error')
    } finally {
      setSaving(false)
    }
  }

  // Loading state
  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-12">
        <div className="flex items-center justify-center gap-3 text-grey">
          <div className="w-5 h-5 border-2 border-gray-300 border-t-primary rounded-full animate-spin" />
          <span className="text-sm">Loading sales channel settings...</span>
        </div>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
        <div className="w-12 h-12 bg-red-50 rounded-xl flex items-center justify-center mx-auto mb-4">
          <svg className="w-6 h-6 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
          </svg>
        </div>
        <p className="text-sm text-grey mb-4">{error}</p>
        <button onClick={loadSettings} className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary/90">
          Try Again
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-secondary">Sales Channel Permissions</h1>
          <p className="text-sm text-grey mt-1">Assign users to sales channels to control who can list vehicles on each platform.</p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving || !hasChanges}
          className="inline-flex items-center gap-2 px-5 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {saving && <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />}
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      {/* User/Channel Matrix Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100">
          <h2 className="text-sm font-semibold text-secondary">Channel Assignments</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50">
                <th className="px-6 py-3 text-left text-xs font-semibold text-secondary uppercase tracking-wider">User</th>
                {CHANNEL_OPTIONS.map((channel) => (
                  <th key={channel.value} className="px-6 py-3 text-center text-xs font-semibold text-secondary uppercase tracking-wider">
                    {channel.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {users.map((user) => (
                <tr key={user.user_id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-secondary">{user.user_name}</div>
                    <div className="text-xs text-grey">{user.user_email}</div>
                  </td>
                  {CHANNEL_OPTIONS.map((channel) => {
                    const checked = channelMap.get(user.user_id)?.has(channel.value) || false
                    return (
                      <td key={channel.value} className="px-6 py-4 text-center">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => handleToggle(user.user_id, channel.value)}
                          aria-label={`${channel.label} for ${user.user_name}`}
                          className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary/30 cursor-pointer"
                        />
                      </td>
                    )
                  })}
                </tr>
              ))}
              {users.length === 0 && (
                <tr>
                  <td colSpan={CHANNEL_OPTIONS.length + 1} className="px-6 py-8 text-center text-sm text-grey">
                    No users found for this account.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
